<template>
  <!-- IMPORTANT iOS Safari note:
       Use a NATIVE <button>, NOT <b-button>. Bootstrap-Vue 2.x running on
       Vue 3 via @vue/compat introduces just enough indirection in the click
       event path to drop iOS Safari's "user activation" context — which
       SpeechRecognition.start() requires. A direct native button keeps the
       gesture context intact. Bootstrap CSS classes give the same visual
       result. Do not "tidy this up" by switching back to b-button. -->
  <button class="btn btn-outline-danger btn-sm voice-record-button" type="button"
    @click="toggleVoiceRecording">
    <i :class="`bi ${isRecording ? 'bi-mic-fill' : 'bi-mic'}`" aria-hidden="true"></i>
    {{ isRecording ? "Stop recording" : (label || "Record voice") }}
  </button>
</template>

<script>
// Voice-only dictation button. Emits @transcript with the recognised text
// and (when v-model is bound to a plain string) also appends to that string.
// Pattern lifted from CoachReportAIHelper but without the AI-refine flow,
// so it can drop into any field — Quill HTML, plain textarea, etc. — and
// the parent decides how to merge.
export default {
  name: "VoiceDictationButton",
  props: {
    // Vue 3 v-model name (preferred).
    modelValue: { type: String, default: undefined },
    // Vue 2 v-model name — accepted as fallback for @vue/compat parents
    // whose v-model may have compiled to the Vue 2 binding shape. The
    // child reads whichever is defined (modelValue wins if both).
    value: { type: String, default: undefined },
    label: { type: String, default: "" },
    lang: { type: String, default: "en-UK" },
  },
  emits: ["update:modelValue", "input", "transcript", "voice-start", "voice-end"],
  computed: {
    // Single source of truth for the current text — prefers Vue 3 prop.
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
  },
  data() {
    return {
      isRecording: false,
      recognition: null,
      // Snapshot of the parent's value at the moment recording started.
      // Each interim onresult emits baseValue + best-transcript-so-far so the
      // text grows naturally in the field as the user speaks.
      baseValue: "",
    };
  },
  beforeUnmount() {
    this.teardownRecognition();
  },
  methods: {
    toggleVoiceRecording() {
      if (this.isRecording) this.stopVoiceRecording();
      else this.startVoiceRecording();
    },
    // Hard teardown: null every handler AND the reference. iOS Safari's
    // SpeechRecognition can get stuck if the previous instance is still
    // holding the mic resource — even a tiny lingering handler reference
    // is enough to wedge subsequent .start() calls into silent no-ops.
    // Called in onend, onerror, stopVoiceRecording, beforeUnmount, AND at
    // the top of startVoiceRecording.
    teardownRecognition() {
      const r = this.recognition;
      if (!r) return;
      console.log("[VoiceDictation] teardownRecognition");
      try { r.onresult = null; } catch (e) { /* ignore */ }
      try { r.onend = null; } catch (e) { /* ignore */ }
      try { r.onerror = null; } catch (e) { /* ignore */ }
      try { r.onstart = null; } catch (e) { /* ignore */ }
      try { r.onnomatch = null; } catch (e) { /* ignore */ }
      try { r.onspeechend = null; } catch (e) { /* ignore */ }
      try { r.stop(); } catch (e) { /* already stopped or never started */ }
      try { r.abort(); } catch (e) { /* same */ }
      this.recognition = null;
    },
    startVoiceRecording() {
      console.log("[VoiceDictation] startVoiceRecording, isRecording was:", this.isRecording, "had old recognition:", !!this.recognition);

      // Hard teardown of any previous instance, sync.
      this.teardownRecognition();

      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        alert(
          "Your browser does not support voice recording. On iPhone, use Safari 14.5 or newer.",
        );
        return;
      }

      let recognition;
      try {
        recognition = new SpeechRecognition();
        console.log("[VoiceDictation] new SpeechRecognition() ok");
      } catch (err) {
        console.error("[VoiceDictation] SpeechRecognition constructor failed:", err);
        alert("Voice recording could not start: " + (err.message || err));
        return;
      }

      recognition.lang = this.lang;
      // CRITICAL: interimResults must be true.
      //
      // iOS Safari has a documented bug: on the first SpeechRecognition
      // session of a page load, .stop() flushes the buffered audio
      // through onresult before onend. On every subsequent session,
      // .stop() discards the unflushed buffer and goes straight to
      // onend — no transcript is delivered.
      //
      // With interimResults: true, onresult fires multiple times AS THE
      // USER SPEAKS (each event has isFinal=false until the very end).
      // We accumulate the transcript-so-far on every interim event, so
      // by the time .stop() is called we already have the text. WebKit
      // can discard the "final" buffer all it wants — we don't need it.
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.continuous = false;

      // Snapshot the parent's current value once, at start. Each interim
      // result emits baseValue + currentTranscript so the field grows as
      // the user speaks, without compounding earlier interim results.
      this.baseValue = this.currentValue || "";

      recognition.onstart = () => {
        console.log("[VoiceDictation] onstart fired — mic is live");
        // Tell parents (e.g. ReviewSection wrapping a Quill editor) that a
        // fresh voice session has begun, so they can snapshot their current
        // content and start replacing into it on subsequent @transcript
        // events.
        this.$emit("voice-start");
      };
      recognition.onspeechstart = () => {
        console.log("[VoiceDictation] onspeechstart — speech detected");
      };
      recognition.onspeechend = () => {
        console.log("[VoiceDictation] onspeechend — speech ended");
      };
      recognition.onresult = (event) => {
        try {
          // Build the best-transcript-so-far by concatenating every result
          // chunk's top alternative — both interim and final.
          let transcriptSoFar = "";
          for (let i = 0; i < event.results.length; i++) {
            transcriptSoFar += event.results[i][0].transcript;
          }
          transcriptSoFar = transcriptSoFar.trim();
          const isFinal = event.results[event.results.length - 1]?.isFinal;
          console.log(
            "[VoiceDictation] onresult fired, isFinal:", isFinal,
            "transcript:", transcriptSoFar,
          );
          if (!transcriptSoFar) return;

          const next =
            `${this.baseValue}${this.baseValue ? " " : ""}${transcriptSoFar}`;

          this.$emit("update:modelValue", next);
          this.$emit("input", next);
          this.$emit("transcript", transcriptSoFar);

          this.$nextTick(() => {
            this.$emit("update:modelValue", next);
            this.$emit("input", next);
          });
        } catch (err) {
          console.error("[VoiceDictation] onresult handler threw:", err);
        }
      };
      recognition.onend = () => {
        console.log("[VoiceDictation] onend fired");
        this.isRecording = false;
        // Tell parents the voice session has ended so they can commit
        // whatever interim text was last received.
        this.$emit("voice-end");
        // Hard teardown so the next .start() gets a fresh instance — iOS
        // Safari often refuses to reuse a finished recognition.
        this.teardownRecognition();
      };
      recognition.onnomatch = () => {
        console.log("[VoiceDictation] onnomatch fired — no recognisable speech");
      };
      recognition.onerror = (event) => {
        console.log("[VoiceDictation] onerror fired:", event.error);
        this.isRecording = false;
        const err = event.error;
        // Tear down BEFORE the alert, so the user can re-tap without the
        // old stuck instance blocking the new attempt.
        this.teardownRecognition();
        if (err === "not-allowed" || err === "service-not-allowed") {
          alert(
            "Microphone access is blocked. On iPhone: Settings → Safari → Microphone, allow access for this site, then reload.",
          );
        } else if (err === "no-speech") {
          // Silent — common case, the user just didn't speak in time. No alert.
        } else if (err === "audio-capture") {
          alert("No microphone was found on this device.");
        } else if (err === "network") {
          alert("A network error occurred. Voice recognition needs an internet connection.");
        } else if (err === "aborted") {
          // Common — happens when we teardown previous instance. No alert.
        } else {
          console.error("[VoiceDictation] unhandled SpeechRecognition error:", err);
          alert("Voice recording error: " + err);
        }
      };

      this.recognition = recognition;
      this.isRecording = true;

      // Defer .start() one event-loop tick. iOS Safari can refuse to start
      // a new recognition session immediately after tearing down the
      // previous one — the audio session needs a moment to release.
      // setTimeout(_, 0) is enough; the click's user-activation context
      // survives one tick.
      setTimeout(() => {
        if (this.recognition !== recognition) {
          // Component was torn down or another start happened in the
          // meantime — abandon this attempt.
          console.log("[VoiceDictation] start() skipped — recognition was replaced");
          return;
        }
        try {
          recognition.start();
          console.log("[VoiceDictation] recognition.start() called");
        } catch (err) {
          this.isRecording = false;
          this.teardownRecognition();
          console.error("[VoiceDictation] recognition.start() threw:", err);
          alert("Voice recording could not start: " + (err.message || err));
        }
      }, 0);
    },
    stopVoiceRecording() {
      console.log("[VoiceDictation] stopVoiceRecording");
      // Polite stop — let onend fire so any pending result lands first.
      if (this.recognition) {
        try { this.recognition.stop(); } catch (e) { /* ignore */ }
      }
      this.isRecording = false;
    },
  },
};
</script>
